import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Clock, Plus, Suitcase } from '@factorialco/f0-react/icons/app'
import {
  TrainingsCatalogDataCollectionDocument,
  TrainingsCatalogFiltersDocument,
  TrainingsTrainingCatalogFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import emptyStateImage from './Empty-state-catalog-trainings-card.png'

export const NewCatalogDataCollection = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const client = useApolloClient()
  const source = useGraphqlDataCollectionSource({
    document: TrainingsCatalogDataCollectionDocument,
    mapData: (data) => data.trainings.trainingsConnection.nodes,
    mapPagination: (data) => {
      return {
        total: data.trainings.trainingsConnection.nodes.length ?? 0,
      }
    },
    mapVariables: ({ filters, search, sortings }) => {
      const filterObject: Record<string, unknown> = {}

      if (filters.competencies && filters.competencies.length > 0) {
        filterObject.competencyId = { in: filters.competencies }
      }

      if (filters.tags && filters.tags.length > 0) {
        filterObject.categoryId = { in: filters.tags }
      }

      if (filters.rangeDates) {
        const trainingsDateFrom =
          filters?.rangeDates &&
          typeof filters.rangeDates === 'object' &&
          'from' in filters.rangeDates &&
          filters.rangeDates.from instanceof Date
            ? filters.rangeDates.from.toISOString().split('T')[0]
            : undefined

        const trainingsDateTo =
          filters?.rangeDates &&
          typeof filters.rangeDates === 'object' &&
          'to' in filters.rangeDates &&
          filters.rangeDates.to instanceof Date
            ? filters.rangeDates.to.toISOString().split('T')[0]
            : undefined

        if (trainingsDateFrom && trainingsDateTo) {
          filterObject.createdAt = { between: [trainingsDateFrom, trainingsDateTo] }
        }
      }
      return {
        search,
        filter: Object.keys(filterObject).length > 0 ? filterObject : undefined,
        status: 'active',
        sort: sortings?.[0],
      }
    },
    filters: {
      rangeDates: {
        label: i18n.t('trainings.my_training.catalog.table.filters.next_session'),
        type: 'date',
        options: {
          mode: 'range',
        },
      },
      tags: {
        label: i18n.t('trainings.my_training.catalog.table.filters.tags'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: TrainingsCatalogFiltersDocument,
              fetchPolicy: 'cache-first',
            })
            return result.data?.trainings.categoriesConnection.nodes.map((category) => ({
              label: category.name,
              value: category.id,
            }))
          },
        },
      },
      competencies: {
        label: i18n.t('trainings.my_training.catalog.table.filters.competencies'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: TrainingsCatalogFiltersDocument,
              fetchPolicy: 'cache-first',
            })
            return result.data?.competencies.competenciesConnection.nodes.map((competency) => ({
              label: competency.name,
              value: competency.id,
            }))
          },
        },
      },
    },
    search: {
      enabled: true,
    },
    itemActions: (item: TrainingsTrainingCatalogFragment) => [
      {
        label: i18n.t('trainings.my_training.catalog.table.actions.request_from_catalog'),
        type: 'primary',
        onClick: () => {
          navigate(
            navigation.myTrainings.catalog.list.fromCatalog.fullPath({ trainingId: item.id })
          )
        },
      },
    ],
    itemUrl: (item: TrainingsTrainingCatalogFragment) =>
      //TODO: Change to the new routes
      navigation.myTrainings.catalog.detail.overview.fullPath({ trainingId: item.id }),
    primaryActions: () => ({
      label: i18n.t('trainings.my_training.catalog.table.actions.request_new_proposal'),
      icon: Plus,
      onClick: () => {
        navigate(navigation.myTrainings.catalog.list.new.fullPath())
      },
    }),
  })

  return (
    <OneDataCollection
      source={source}
      emptyStates={{
        'no-data': {
          title: i18n.t('trainings.my_training.catalog.table.empty-state.title'),
          description: i18n.t('trainings.my_training.catalog.table.empty-state.description'),
        },
      }}
      visualizations={[
        {
          type: 'card',
          options: {
            title: (item) => item.name,
            image: (item) => item.thumbnail ?? emptyStateImage,
            cardProperties: [
              {
                label: '',
                icon: Clock,
                render: ({ totalDuration }) => {
                  const hours = Math.floor(totalDuration ?? 0)
                  const minutes = Math.round(((totalDuration ?? 0) % 1) * 60)
                  return totalDuration
                    ? i18n.t('trainings.my_training.catalog.table.total_duration_split', {
                        hours,
                        minutes,
                      })
                    : i18n.t('trainings.my_training.catalog.table.total_duration_empty')
                },
              },
              {
                label: '',
                icon: Calendar,
                render: ({ trainingClasses }) => {
                  // Find the earliest startDate and latest endDate
                  const datesWithValues = trainingClasses.filter((tc) => tc.startDate || tc.endDate)

                  if (datesWithValues.length === 0) {
                    return {
                      type: 'text',
                      value: i18n.t('trainings.my_training.catalog.table.dates_empty'),
                    }
                  } else {
                    const earliestStartDate = datesWithValues
                      .map((tc) => tc.startDate)
                      .filter((date): date is string => Boolean(date))
                      .sort()[0]

                    const latestEndDate = datesWithValues
                      .map((tc) => tc.endDate)
                      .filter((date): date is string => Boolean(date))
                      .sort()
                      .reverse()[0]

                    if (earliestStartDate && latestEndDate) {
                      return {
                        type: 'text',
                        value: `${i18nDateToString(
                          moment(earliestStartDate),
                          'ui_date_day_month_year'
                        )} - ${i18nDateToString(moment(latestEndDate), 'ui_date_day_month_year')}`,
                      }
                    }
                  }
                },
              },
              {
                label: '',
                icon: Suitcase,
                render: ({ competencies }) => {
                  if (competencies.length === 0) {
                    return {
                      type: 'text',
                      value: i18n.t('trainings.my_training.catalog.table.competencies_empty'),
                    }
                  } else {
                    return {
                      type: 'tagList',
                      emptyValue: i18n.t('trainings.my_training.catalog.table.competencies_empty'),
                      value: {
                        type: 'raw',
                        truncate: true,
                        max: 2,
                        tags: competencies.map((competency) => ({
                          text: competency.name,
                        })),
                      },
                    }
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
